import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Send } from "lucide-react";
import api from "@/lib/api";

const STATUS_STYLES = {
  sent: "bg-emerald-100 text-emerald-800 border-emerald-300",
  failed: "bg-red-100 text-red-800 border-red-300",
  skipped_disabled: "bg-amber-100 text-amber-800 border-amber-300",
  skipped_duplicate: "bg-slate-100 text-slate-700 border-slate-300",
  pending: "bg-blue-100 text-blue-800 border-blue-300",
};

const EVENT_LABELS = {
  test: "Test email",
  registration: "Customer registration",
  quote_sent: "Quote sent",
  quote_approved_booking: "Quote approved + booking",
  payment_received: "Payment received",
  enquiry_admin: "Enquiry alert (admin)",
  guest_quote_confirmed: "Guest quote confirmed (admin)",
  admin_password_reset: "Admin password reset link",
  customer_password_reset: "Customer password reset link",
};

export default function AdminEmailLog() {
  const [config, setConfig] = useState(null);
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [c, n] = await Promise.all([
      api.get("/admin/email/config"),
      api.get("/admin/email/notifications"),
    ]);
    setConfig(c.data);
    setRows(n.data);
  };
  useEffect(() => {
    load().catch(() => toast.error("Failed to load email log"));
  }, []);

  const sendTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/email/test");
      if (data.status === "sent") toast.success(`Test email sent to ${data.sent_to?.join(", ")}`);
      else if (data.status === "skipped_disabled") toast.info("Email sending is disabled (kill switch off) — event was logged only");
      else toast.error(`Test email ${data.status}: ${data.error || ""}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test send failed");
    } finally {
      setBusy(false);
    }
  };

  const sendSamples = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/email/preview-all");
      const sent = data.results.filter((r) => r.status === "sent").length;
      const skipped = data.results.filter((r) => r.status === "skipped_disabled").length;
      if (sent === 6) toast.success("All 6 sample emails sent — check the inbox");
      else if (skipped === 6) toast.info("Email sending is disabled (kill switch off) — 6 sample events logged only");
      else toast.warning(`Samples: ${sent} sent, ${data.results.length - sent} not sent — see log`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sample send failed");
    } finally {
      setBusy(false);
    }
  };

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return iso || "—";
    }
  };

  return (
    <div className="mx-auto max-w-5xl" data-testid="admin-email-log-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="kbd-eyebrow">Admin</span>
          <h1 className="mt-3 font-serif-display text-3xl font-semibold text-secondary">Email log</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Every email event: what triggered it, who it went to, when, and whether it succeeded.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            data-testid="admin-email-refresh-btn"
            onClick={() => load().catch(() => toast.error("Refresh failed"))}
            className="rounded-full border border-border/70 bg-background px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-secondary/90 hover:border-primary/50"
          >
            <RefreshCw className="mr-1 inline h-3.5 w-3.5" /> Refresh
          </button>
          <button
            type="button"
            data-testid="admin-email-send-samples-btn"
            onClick={sendSamples}
            disabled={busy}
            className="rounded-full border border-primary/50 bg-primary/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-primary hover:bg-primary/10"
          >
            {busy ? "Sending…" : "Send all 6 samples"}
          </button>
          <button
            type="button"
            data-testid="admin-email-send-test-btn"
            onClick={sendTest}
            disabled={busy}
            className="btn-primary !py-2 !text-xs"
          >
            <Send className="h-3.5 w-3.5" /> {busy ? "Sending…" : "Send test email"}
          </button>
        </div>
      </div>

      {config && (
        <div className="mt-6 flex flex-wrap gap-2 text-xs" data-testid="admin-email-config-summary">
          <span className={`rounded-full border px-3 py-1 font-semibold ${config.enabled ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-800"}`}>
            Sending: {config.enabled ? "ENABLED" : "DISABLED (kill switch off)"}
          </span>
          <span className={`rounded-full border px-3 py-1 font-semibold ${config.sandbox ? "border-blue-300 bg-blue-50 text-blue-800" : "border-red-300 bg-red-50 text-red-800"}`}>
            {config.sandbox ? `Sandbox: all mail → ${config.sandbox_recipient}` : "LIVE MODE: real customers receive email"}
          </span>
          <span className="rounded-full border border-border/70 bg-muted/40 px-3 py-1 text-secondary/80">
            From: {config.from} · Reply-to: {config.reply_to}
          </span>
        </div>
      )}

      <div className="mt-6 overflow-x-auto rounded-2xl border border-border/70 bg-card">
        <table className="w-full text-left text-sm" data-testid="admin-email-log-table">
          <thead>
            <tr className="border-b border-border/70 text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
              <th className="px-4 py-3">Event</th>
              <th className="px-4 py-3">Intended for</th>
              <th className="px-4 py-3">Actually sent to</th>
              <th className="px-4 py-3">Date / time</th>
              <th className="px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows === null ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr data-testid="admin-email-log-empty">
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                  No email events yet. They will appear here as soon as an event fires (or send a test email).
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id} className="border-b border-border/40 align-top" data-testid={`admin-email-log-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-secondary">{EVENT_LABELS[r.event_type] || r.event_type}</div>
                    <div className="mt-0.5 max-w-[240px] truncate text-xs text-muted-foreground" title={r.subject}>{r.subject}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{(r.to || []).join(", ") || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">
                    {(r.sent_to || []).join(", ") || "—"}
                    {r.sandbox && r.status === "sent" && (
                      <span className="ml-1 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-bold text-blue-800">SANDBOX</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs">{fmtDate(r.created_at)}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full border px-2.5 py-1 text-[11px] font-bold ${STATUS_STYLES[r.status] || "bg-muted text-secondary border-border"}`}>
                      {r.status.replace(/_/g, " ")}
                    </span>
                    {r.error && <div className="mt-1 max-w-[200px] text-[11px] text-red-700" title={r.error}>{r.error}</div>}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
